import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import type { NodeProps, Node } from '@xyflow/react'
import { Box, MapPin } from 'lucide-react'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'

// Define the node data type
type ActivityNodeData = {
  activity: ActivityWithExchanges
  label: string
  selected?: boolean
  isNew?: boolean
}

// Define the full node type
export type ActivityNodeType = Node<ActivityNodeData, 'activity'>

function ActivityNodeComponent(props: NodeProps<ActivityNodeType>) {
  const { activity, selected, isNew } = props.data
  const exchangeCount = activity.exchanges?.length || 0

  return (
    <div
      className={`bg-indigo-500/90 border-2 rounded-lg shadow-lg min-w-[260px] max-w-[320px] transition-all duration-150 ${
        selected
          ? 'border-white ring-2 ring-white/50 ring-offset-2 ring-offset-transparent shadow-xl scale-[1.02]'
          : 'border-indigo-400'
      } ${isNew ? 'node-highlight-new' : ''}`}
    >
      {/* Handles for connections */}
      <Handle type="target" position={Position.Top} id="top" className="!bg-blue-400" />
      <Handle type="target" position={Position.Left} id="left" className="!bg-blue-400" />
      <Handle type="source" position={Position.Bottom} id="bottom" className="!bg-green-400" />
      <Handle type="source" position={Position.Right} id="right" className="!bg-amber-400" />

      {/* Header */}
      <div className="px-3 py-2 border-b border-indigo-400/50">
        <div className="flex items-center gap-2">
          <Box className="w-4 h-4 text-indigo-200" />
          <span className="text-xs font-medium text-indigo-200 uppercase tracking-wide">
            Activity
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 py-2">
        <h3 className="text-sm font-semibold text-white leading-tight line-clamp-2">
          {activity.name}
        </h3>

        <div className="mt-2 flex items-center gap-3 text-xs text-indigo-200">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {activity.location}
          </span>
          <span className="px-1.5 py-0.5 bg-indigo-600/50 rounded">{activity.unit}</span>
        </div>

        <div className="mt-2 text-[10px] text-indigo-300 font-mono truncate">{activity.code}</div>
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 bg-indigo-600/30 rounded-b-lg">
        <span className="text-[10px] text-indigo-200">
          {exchangeCount} exchange{exchangeCount !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  )
}

export const ActivityNode = memo(ActivityNodeComponent)
